// ChatPane — scrolling transcript + composer textarea. Lives inside the
// floating chat slide-over (UI_SPEC §1.18.b), so it's light-themed to
// match the white shell surface.
//
// Owns no message state; the parent (ChatPill) supplies `messages` from
// the useChat hook in ShellContext and routes the typed value back via
// `onSend` / `onInputChange`.

import { useEffect, useRef, useState } from "react";
import type { ChatMessage } from "../lib/chat.js";
import { renderMarkdownLite } from "../lib/markdown.js";

interface Props {
  messages: ChatMessage[];
  sending: boolean;
  onSend: (text: string) => void;
  inputValue: string;
  onInputChange: (v: string) => void;
}

const QUICK_CHIPS = [
  "Show the project dashboard.",
  "Show the Gantt chart.",
  "List the project risks.",
  "Show the RCPM critical path.",
  "Show the work-breakdown structure.",
];

export function ChatPane({ messages, sending, onSend, inputValue, onInputChange }: Props) {
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, messages.at(-1)?.text]);

  return (
    <div className="flex h-full flex-col bg-white">
      <div ref={transcriptRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {messages.length === 0 && (
          <>
            <p className="px-2 py-3 text-sm italic text-slate-500">
              Ask about the project. Or pick a quick action:
            </p>
            <div className="flex flex-wrap gap-2 px-2">
              {QUICK_CHIPS.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700 hover:bg-slate-100"
                  onClick={() => { onSend(q); }}
                >{q}</button>
              ))}
            </div>
          </>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} />)}
        {sending && <div className="px-2 py-1 text-xs text-slate-500">…</div>}
      </div>
      <Composer
        sending={sending}
        value={inputValue}
        onChange={onInputChange}
        onSubmit={() => { onSend(inputValue); onInputChange(""); }}
      />
    </div>
  );
}

function Bubble({ m }: { m: ChatMessage }) {
  const base = "rounded-md border px-3 py-2 text-sm leading-relaxed";
  const cls =
    m.role === "user"
      ? `${base} ml-auto max-w-[90%] border-sky-200 bg-sky-50 text-slate-900`
      : m.role === "assistant"
        ? `${base} max-w-[95%] border-slate-200 bg-white text-slate-900`
        : m.role === "tool"
          ? `${base} max-w-[95%] border-emerald-200 bg-emerald-50/60 font-mono text-xs text-slate-700`
          : `${base} max-w-[95%] border-rose-200 bg-rose-50 text-xs text-rose-700`;
  const isHtml = m.role === "tool";
  const errorHint = m.role === "error" ? friendlyError(m.text) : null;
  return (
    <div className={cls}>
      {m.meta && <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{m.meta}</div>}
      {m.role === "user"
        ? <span className="whitespace-pre-wrap">{m.text}</span>
        : m.role === "assistant"
          ? <span dangerouslySetInnerHTML={{ __html: renderMarkdownLite(m.text) }} />
          : isHtml
            ? <span dangerouslySetInnerHTML={{ __html: m.text }} />
            : errorHint
              ? <div>
                  <div className="font-medium">{errorHint.title}</div>
                  <div className="mt-1 text-rose-600">{errorHint.hint}</div>
                  <details className="mt-2 cursor-pointer">
                    <summary className="text-[10px] uppercase tracking-wider text-rose-500">raw error</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[10px] text-rose-600">{m.text}</pre>
                  </details>
                </div>
              : <span className="font-mono">{m.text}</span>}
    </div>
  );
}

// Turn the raw Anthropic / network error blob into a one-line hint a
// non-technical user can act on. Best-effort regex match; everything
// else is presented as the raw blob in a collapsed <details>.
function friendlyError(text: string): { title: string; hint: string } | null {
  if (/401|authentication_error|Invalid authentication credentials/i.test(text)) {
    return {
      title: "Chat unavailable — invalid credentials",
      hint: "The Anthropic OAuth token configured on this server has expired or is invalid. The other views still work; only chat is offline until the token is refreshed.",
    };
  }
  if (/429|rate.?limit/i.test(text)) {
    return {
      title: "Chat is rate-limited",
      hint: "Too many recent requests. Wait a moment and try again.",
    };
  }
  if (/connect|ECONN|fetch.*failed|NetworkError|Failed to fetch/i.test(text)) {
    return {
      title: "Cannot reach the chat backend",
      hint: "The canvas backend or the helper server isn't responding. Other views may fail too.",
    };
  }
  if (/503|config invalid/i.test(text)) {
    return {
      title: "Chat backend not configured",
      hint: "The server is missing required credentials. Check the deployment env.",
    };
  }
  return null;
}

interface ComposerProps {
  sending: boolean;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}

function Composer({ sending, value, onChange, onSubmit }: ComposerProps) {
  const [rows, setRows] = useState(2);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!sending) onSubmit(); }}
      className="flex gap-2 border-t border-slate-200 bg-slate-50 p-3"
    >
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setRows(3)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            if (!sending) onSubmit();
          }
        }}
        placeholder="Ask about the project — Gantt, risks, what-if, …"
        className="flex-1 resize-none rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none"
      />
      <button
        type="submit"
        disabled={sending || !value.trim()}
        className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-wait disabled:bg-slate-300"
      >
        {sending ? "…" : "Send"}
      </button>
    </form>
  );
}
